/**
 * Source Recommendation Banner
 *
 * Shows personalised source recommendations above the chat input,
 * based on the user's country and specialty. Users can Apply (creates a source list),
 * Customise (opens create collection wizard with pre-populated domains), or Dismiss.
 *
 * Figma: https://www.figma.com/design/PiRSH2ORtexkp82fSPpi9Z/Evidence-sign-up---adoption?node-id=8141-20298
 */

import { useSourceRecommendationBanner } from '@/hooks/useSourceRecommendationBanner';
import { useTeamEvidenceEnforcement } from '@heidi/evidence';
import { getFaviconUrl } from '@/utils/faviconCache';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { CreateCollectionWizard } from '@heidi/evidence';
import { cn } from '@/lib/cn';
import { TextSearch, X } from 'lucide-react';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

/**
 * Extract a short label for a source.
 * Priority: parenthesized acronym > leading uppercase acronym > short name > truncated.
 *
 * Examples:
 *   "National Institute ... (NICE)" → "NICE"
 *   "BMJBestPractice"               → "BMJ"
 *   "UpToDate"                      → "UpToDate"
 *   "RACGP"                         → "RACGP"
 */
function getShortLabel(displayName: string, websiteName: string | string[]): string {
  const name = typeof websiteName === 'string' ? websiteName : websiteName[0] ?? '';
  // 1. Try extracting acronym from parentheses in either field
  for (const candidate of [name, displayName]) {
    const match = candidate.match(/\(([A-Z][A-Za-z0-9-]{1,10})\)/);
    if (match?.[1]) return match[1];
  }
  // 2. If short enough, use as-is
  if (displayName.length <= 12) return displayName;
  // 3. Extract leading uppercase acronym from CamelCase (e.g. "BMJBestPractice" → "BMJ")
  const leadingAcronym = displayName.match(/^([A-Z]{2,})(?=[A-Z][a-z])/);
  if (leadingAcronym?.[1]) return leadingAcronym[1];
  // 4. Truncate
  return displayName.slice(0, 10);
}

interface SourceRecommendationBannerProps {
  className?: string;
  location?: 'chat' | 'popover' | 'session';
}

/** Inline favicon with fallback */
function SourceBadgeFavicon({ domain }: { domain: string }) {
  const [hasError, setHasError] = useState(false);
  if (hasError) return null;
  return (
    <Image
      src={getFaviconUrl(domain)}
      alt=""
      width={16}
      height={16}
      unoptimized
      className="size-4 shrink-0 rounded-sm"
      onError={() => setHasError(true)}
    />
  );
}

export function SourceRecommendationBanner({
  className,
  location = 'chat',
}: SourceRecommendationBannerProps) {
  const intl = useIntl();
  const {
    shouldShow,
    sources,
    matchedCountry,
    specialtyName,
    isApplying,
    onApply,
    onCustomise,
    onDismiss,
    isCustomiseOpen,
    setIsCustomiseOpen,
    onCustomiseComplete,
    recommendedDomains,
    defaultCollectionName,
  } = useSourceRecommendationBanner(location);
  // The wizard's team-scoping facts, resolved here: `useTeamEvidenceEnforcement`
  // is `src/hooks/`, which is not a product glob, so Chat may read it.
  const { canCreateTeamCollection, teamName } = useTeamEvidenceEnforcement();
  const wizardTeamContext = useMemo(
    () => ({ canCreateTeamCollection, teamName }),
    [canCreateTeamCollection, teamName]
  );

  return (
    <>
      {shouldShow && (
        <div
          role="region"
          aria-label={intl.formatMessage({ defaultMessage: 'Source recommendations', id: 'a/CC3Z/G8Z' })}
          data-testid="source-recommendation-banner"
          className={cn(
            'flex w-full items-center justify-between gap-4 rounded-lg border border-border bg-surface-accent p-4 shadow-sm',
            className
          )}
        >
          {/* Left: icon + text + source badges */}
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <TextSearch className="mt-0.5 size-5 shrink-0 text-text-primary" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <TypographyP2Bold className="text-text-primary">
                <FormattedMessage
                  defaultMessage="Get clinical answers from trusted sources"
                  id="kR7vXmN2Yt"
                />
              </TypographyP2Bold>
              <div className="flex items-center gap-3 overflow-hidden">
                {sources.slice(0, 4).map((source) => (
                  <div
                    key={source.domain}
                    className="flex shrink-0 items-center gap-0.5"
                  >
                    <SourceBadgeFavicon domain={source.domain} />
                    <TypographyCaption className="text-text-secondary">
                      {getShortLabel(source.display_name, source.website_name)}
                    </TypographyCaption>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex shrink-0 items-center gap-2">
            <div className="flex flex-col-reverse items-stretch gap-2 lg:flex-row lg:items-center">
              <Button
                variant="outline"
                size="xs"
                onClick={onCustomise}
                type="button"
              >
                <FormattedMessage defaultMessage="Customize" id="TXpOBiuxud" />
              </Button>
              <Button
                variant="default"
                size="xs"
                onClick={onApply}
                disabled={isApplying}
                type="button"
              >
                <FormattedMessage defaultMessage="Apply" id="EWw/tKINJT" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              onClick={onDismiss}
              type="button"
              aria-label={intl.formatMessage({ defaultMessage: 'Dismiss source recommendations', id: 'AXdO8ksm+g' })}
            >
              <X size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Create collection wizard — rendered outside shouldShow guard so it
          stays mounted while the onCollectionCreated callback fires and dismisses the banner */}
      {(shouldShow || isCustomiseOpen) && (
        <CreateCollectionWizard
          open={isCustomiseOpen}
          onOpenChange={setIsCustomiseOpen}
          initialStep="web-sources"
          initialDomains={recommendedDomains}
          initialName={defaultCollectionName}
          saveLabel={intl.formatMessage({ defaultMessage: 'Save & Apply', id: 'hYCUSNu+/I' })}
          onCollectionCreated={onCustomiseComplete}
          teamContext={wizardTeamContext}
        />
      )}
    </>
  );
}
